import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { api } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Toast, { ToastType } from '../../components/ui/Toast';
import {
  Prospect,
  TimelineEntry,
  ProspectTitle,
  PlaceOfWork,
  COMPANY_LICENSES,
  STATE_ABBREVIATIONS,
  formatPhone,
} from '../../types';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PlayIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  XMarkIcon,
  ArrowUpIcon,
  UserPlusIcon,
  EyeSlashIcon,
  DocumentTextIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import UpcomingProspects from '../../components/admin/UpcomingProspects';

// ==================== Stage Configuration ====================

type PipelineStage =
  | 'recruit'
  | 'pending'
  | 'email_created'
  | 'onboarding_sent'
  | 'form_submitted'
  | 'paid'
  | 'onboarding'
  | 'sponsored'
  | 'training'
  | 'live'
  | 'unsponsored';

const stageConfig: Record<PipelineStage, { label: string; color: string; order: number }> = {
  recruit: { label: 'Recruit', color: 'bg-slate-100 text-slate-700', order: 1 },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', order: 2 },
  email_created: { label: 'Email Created', color: 'bg-blue-100 text-blue-700', order: 3 },
  onboarding_sent: { label: 'Onboarding Sent', color: 'bg-purple-100 text-purple-700', order: 4 },
  form_submitted: { label: 'Form Submitted', color: 'bg-indigo-100 text-indigo-700', order: 5 },
  paid: { label: 'Paid', color: 'bg-teal-100 text-teal-700', order: 6 },
  onboarding: { label: 'Onboarding', color: 'bg-cyan-100 text-cyan-700', order: 7 },
  sponsored: { label: 'Sponsored', color: 'bg-sky-100 text-sky-700', order: 8 },
  training: { label: 'Training', color: 'bg-amber-100 text-amber-700', order: 9 },
  live: { label: 'Live', color: 'bg-emerald-100 text-emerald-700', order: 10 },
  unsponsored: { label: 'Unsponsored', color: 'bg-rose-100 text-rose-700', order: 0 },
};

// Pre-conversion stages (have action buttons)
const preConversionStages: PipelineStage[] = [
  'recruit', 'pending', 'email_created', 'onboarding_sent', 'form_submitted', 'paid'
];

// ==================== Types ====================

interface ProspectWithTimeline {
  prospect: Prospect;
  timeline: TimelineEntry | null;
  stage: PipelineStage;
}

type SortColumn =
  | 'month'
  | 'name'
  | 'stage'
  | 'contact'
  | 'nmls'
  | 'states'
  | 'company_email'
  | 'start_date'
  | 'sponsor_date'
  | 'training_date'
  | 'live_date'
  | 'unsponsor_date';

type SortDirection = 'asc' | 'desc';

// ==================== Helper Functions ====================

function getStage(prospect: Prospect, timeline: TimelineEntry | null): PipelineStage {
  // Pre-conversion prospects use their status directly
  // Sponsorship/training/live dates don't apply until they've completed onboarding
  if (prospect.status !== 'converted') {
    return prospect.status as PipelineStage;
  }

  // Post-conversion: check timeline dates from latest milestone to earliest
  const today = new Date().toISOString().split('T')[0];

  // Live takes priority - if they went live, they stay live
  if (timeline?.leads_live_date && timeline.leads_live_date <= today) return 'live';
  if (timeline?.training_date && timeline.training_date <= today) return 'training';
  if (timeline?.sponsor_date && timeline.sponsor_date <= today) return 'sponsored';

  // Unsponsored = left AFTER being sponsored (must have sponsor_date to be unsponsored)
  if (timeline?.unsponsor_date && timeline?.sponsor_date) return 'unsponsored';

  // Converted but no timeline dates yet = onboarding
  return 'onboarding';
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year.slice(2)}`;
}

function formatMonthYear(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}

// Get the relevant month date for an entry (sponsor_date for post-conversion, expected_start_date for pre-conversion)
function getMonthDate(entry: ProspectWithTimeline): string | null {
  const isPostConversion = !preConversionStages.includes(entry.stage);
  if (isPostConversion) {
    return entry.timeline?.sponsor_date || null;
  }
  return entry.prospect.expected_start_date || null;
}

function getRowHighlight(entry: ProspectWithTimeline): string {
  const sponsorDate = entry.timeline?.sponsor_date;
  if (!sponsorDate) return '';

  const sponsor = new Date(sponsorDate);
  const today = new Date();
  const daysUntil = Math.ceil((sponsor.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0 && !entry.timeline?.leads_live_date) {
    return 'border-l-4 border-l-rose-400 bg-rose-50/50';
  }
  if (daysUntil >= 0 && daysUntil <= 7) {
    return 'border-l-4 border-l-amber-400 bg-amber-50/30';
  }
  return '';
}

function formatStateAbbreviations(states: string[] | null): string {
  if (!states || states.length === 0) return '';
  return states
    .map(state => STATE_ABBREVIATIONS[state] || state)
    .sort()
    .join(', ');
}

// ==================== Popover Component ====================

const HoverPopover: React.FC<{
  trigger: React.ReactNode;
  content: React.ReactNode;
  title?: string;
}> = ({ trigger, content, title }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {trigger}
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 min-w-[160px] max-w-[280px]">
          <div className="bg-slate-800 text-white text-xs rounded-lg shadow-lg p-3">
            {title && (
              <div className="font-semibold text-slate-300 mb-1 text-[10px] uppercase tracking-wider">
                {title}
              </div>
            )}
            <div className="text-slate-100">{content}</div>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-800 rotate-45" />
        </div>
      )}
    </div>
  );
}

// ==================== Component ====================

const Prospects: React.FC = () => {
  const { loanOfficer: currentAdmin, isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const navigate = useNavigate();

  // Data state
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [hideLive, setHideLive] = useState(true);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('month');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Inline editing state
  const [editingEmails, setEditingEmails] = useState<Map<string, string>>(new Map());
  const [editingExistingEmails, setEditingExistingEmails] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // Modal state
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [deletingProspect, setDeletingProspect] = useState<Prospect | null>(null);
  const [deleteAlsoProfile, setDeleteAlsoProfile] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    preferred_first_name: '',
    title: 'Sr Mortgage Broker' as ProspectTitle,
    phone: '',
    email: '',
    nmls: '',
    place_of_work: '' as PlaceOfWork | '',
    expected_month: '',
    expected_start_date: '',
    state_licenses: [] as string[],
    notes: '',
  });
  const [editLicenseDropdownOpen, setEditLicenseDropdownOpen] = useState(false);
  const editLicenseDropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editLicenseDropdownRef.current && !editLicenseDropdownRef.current.contains(event.target as Node)) {
        setEditLicenseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [prospectsData, timelineData] = await Promise.all([
        api.getProspects(),
        api.getTimelineEntries(),
      ]);
      setProspects(prospectsData || []);
      setTimelineEntries(timelineData || []);
    } catch (err: any) {
      showToast(err.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge prospects with timeline entries and calculate stages
  const mergedData = useMemo(() => {
    const timelineByProspectId = new Map<string, TimelineEntry>();
    timelineEntries.forEach(t => {
      timelineByProspectId.set(t.prospect_id, t);
    });

    return prospects.map(prospect => {
      const timeline = timelineByProspectId.get(prospect.id) || null;
      const stage = getStage(prospect, timeline);
      return { prospect, timeline, stage };
    });
  }, [prospects, timelineEntries]);

  // Sort helper function
  const getSortValue = (entry: ProspectWithTimeline, column: SortColumn): string | number | null => {
    const { prospect, timeline, stage } = entry;
    switch (column) {
      case 'month':
        return getMonthDate(entry) || '';
      case 'name':
        return `${prospect.first_name} ${prospect.last_name}`.toLowerCase();
      case 'stage':
        return stageConfig[stage].order;
      case 'contact':
        return (prospect.email || prospect.phone || '').toLowerCase();
      case 'nmls':
        return prospect.nmls || '';
      case 'states':
        return prospect.state_licenses?.length || 0;
      case 'company_email':
        return prospect.company_email || '';
      case 'start_date':
        return prospect.expected_start_date || '';
      case 'sponsor_date':
        return timeline?.sponsor_date || '';
      case 'training_date':
        return timeline?.training_date || '';
      case 'live_date':
        return timeline?.leads_live_date || '';
      case 'unsponsor_date':
        return timeline?.unsponsor_date || '';
      default:
        return '';
    }
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...mergedData];

    // Hide live if toggled
    if (hideLive) {
      result = result.filter(e => e.stage !== 'live');
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e => {
        const name = `${e.prospect.first_name} ${e.prospect.last_name}`.toLowerCase();
        const email = (e.prospect.email || e.prospect.company_email || '').toLowerCase();
        const phone = (e.prospect.phone || '').toLowerCase();
        const nmls = (e.prospect.nmls || '').toLowerCase();
        return name.includes(q) || email.includes(q) || phone.includes(q) || nmls.includes(q);
      });
    }

    // Sort by selected column
    result.sort((a, b) => {
      const aVal = getSortValue(a, sortColumn);
      const bVal = getSortValue(b, sortColumn);

      // Handle nulls - push to end
      if (aVal === null || aVal === '') return sortDirection === 'asc' ? 1 : -1;
      if (bVal === null || bVal === '') return sortDirection === 'asc' ? -1 : 1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [mergedData, hideLive, searchQuery, sortColumn, sortDirection]);

  // Stage counts for badges
  const stageCounts = useMemo(() => {
    const counts: Record<PipelineStage, number> = {} as any;
    Object.keys(stageConfig).forEach(stage => {
      counts[stage as PipelineStage] = mergedData.filter(e => e.stage === stage).length;
    });
    return counts;
  }, [mergedData]);

  // ==================== Sort Handler ====================

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // ==================== Action Handlers ====================

  const handleStartOnboarding = async (prospectId: string) => {
    if (!currentAdmin?.id) {
      showToast('User not authenticated', 'error');
      return;
    }

    const prospect = prospects.find(p => p.id === prospectId);
    if (!prospect) {
      showToast('Prospect not found', 'error');
      return;
    }

    const missingFields: string[] = [];
    if (!prospect.first_name) missingFields.push('First Name');
    if (!prospect.last_name) missingFields.push('Last Name');
    if (!prospect.phone) missingFields.push('Phone');
    if (!prospect.email) missingFields.push('Personal Email');
    if (!prospect.nmls) missingFields.push('NMLS Number');
    if (!prospect.expected_start_date) missingFields.push('Expected Start Date');
    if (!prospect.state_licenses || prospect.state_licenses.length === 0) {
      missingFields.push('State Licenses (at least one)');
    }

    if (missingFields.length > 0) {
      showToast(`Cannot start onboarding. Missing required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }

    setProcessing(prospectId);

    try {
      await api.startProspectOnboarding({
        prospect_id: prospectId,
        admin_id: currentAdmin.id,
      });
      await loadData();
      showToast('Onboarding started successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to start onboarding', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handlePromoteRecruit = async (prospectId: string) => {
    if (!currentAdmin?.id) {
      showToast('User not authenticated', 'error');
      return;
    }

    setProcessing(prospectId);

    try {
      await api.promoteRecruit({
        prospect_id: prospectId,
        admin_id: currentAdmin.id,
      });
      await loadData();
      showToast('Recruit promoted to prospect! Admins notified.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to promote recruit', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleEmailInputChange = (id: string, value: string) => {
    const map = new Map(editingEmails);
    map.set(id, value);
    setEditingEmails(map);
  };

  const handleEditEmail = (prospect: Prospect) => {
    const map = new Map(editingEmails);
    map.set(prospect.id, prospect.company_email || '');
    setEditingEmails(map);
    setEditingExistingEmails(prev => new Set(prev).add(prospect.id));
  };

  const handleCancelEditEmail = (id: string) => {
    const map = new Map(editingEmails);
    map.delete(id);
    setEditingEmails(map);
    setEditingExistingEmails(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const handleSaveEmail = async (prospect: Prospect) => {
    if (!currentAdmin?.id) {
      showToast('User not authenticated', 'error');
      return;
    }
    const email = (editingEmails.get(prospect.id) || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      showToast('Enter an email before saving', 'error');
      return;
    }
    if (!emailRegex.test(email)) {
      showToast('Enter a valid email address', 'error');
      return;
    }

    setProcessing(prospect.id);

    try {
      await api.updateProspectEmail({
        prospect_id: prospect.id,
        company_email: email,
        admin_id: currentAdmin.id,
      });
      const map = new Map(editingEmails);
      map.delete(prospect.id);
      setEditingEmails(map);
      setEditingExistingEmails(prev => { const s = new Set(prev); s.delete(prospect.id); return s; });
      await loadData();
      showToast('Email saved successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to save email', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleSendEmail = async (prospectId: string) => {
    setProcessing(prospectId);

    try {
      await api.sendProspectOnboardingEmail({ prospect_id: prospectId });
      await loadData();
      showToast('Onboarding email sent successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send email', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleResendWelcomeEmail = async (prospectId: string) => {
    setProcessing(prospectId);

    try {
      await api.sendRecruitWelcomeEmail({ prospect_id: prospectId });
      await loadData();
      showToast('Welcome email resent successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to resend welcome email', 'error');
    } finally {
      setProcessing(null);
    }
  };

  // ==================== Inline Date Editing ====================

  const handleStartDateEdit = (timelineId: string, field: string, currentValue: string | null) => {
    setEditingCell({ id: timelineId, field });
    setEditValue(currentValue || '');
  };

  const handleSaveDateEdit = async () => {
    if (!editingCell || !currentAdmin) return;

    const { id, field } = editingCell;
    const updates: Record<string, string | null> = {
      [field]: editValue || null,
    };

    try {
      setSaving(id);
      await api.updateTimelineEntry(id, updates, currentAdmin.id);
      setTimelineEntries(prev => prev.map(e =>
        e.id === id ? { ...e, ...updates } : e
      ));
      showToast('Saved', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to save', 'error');
    } finally {
      setSaving(null);
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleCancelDateEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleDateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveDateEdit();
    else if (e.key === 'Escape') handleCancelDateEdit();
  };

  // ==================== Edit Modal ====================

  const handleOpenEdit = (prospect: Prospect) => {
    setEditingProspect(prospect);
    setEditFormData({
      first_name: prospect.first_name,
      last_name: prospect.last_name,
      preferred_first_name: prospect.preferred_first_name || '',
      title: prospect.title,
      phone: prospect.phone || '',
      email: prospect.email || '',
      nmls: prospect.nmls || '',
      place_of_work: prospect.place_of_work || '',
      expected_month: prospect.expected_month || '',
      expected_start_date: prospect.expected_start_date || '',
      state_licenses: prospect.state_licenses || [],
      notes: prospect.notes || '',
    });
  };

  const handleEditLicenseToggle = (state: string) => {
    setEditFormData(prev => ({
      ...prev,
      state_licenses: prev.state_licenses.includes(state)
        ? prev.state_licenses.filter(s => s !== state)
        : [...prev.state_licenses, state],
    }));
  };

  const handleEditRemoveLicense = (state: string) => {
    setEditFormData(prev => ({
      ...prev,
      state_licenses: prev.state_licenses.filter(s => s !== state),
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingProspect) return;
    if (!editFormData.first_name.trim() || !editFormData.last_name.trim()) {
      showToast('First and last name are required', 'error');
      return;
    }
    if (!editFormData.phone.trim() && !editFormData.email.trim()) {
      showToast('Provide at least one contact method', 'error');
      return;
    }

    setProcessing(editingProspect.id);

    try {
      await api.updateProspect({
        prospect_id: editingProspect.id,
        first_name: editFormData.first_name.trim(),
        last_name: editFormData.last_name.trim(),
        preferred_first_name: editFormData.preferred_first_name.trim() || null,
        title: editFormData.title,
        phone: editFormData.phone.trim() || null,
        email: editFormData.email.trim() || null,
        nmls: editFormData.nmls.trim() || null,
        place_of_work: editFormData.place_of_work || null,
        expected_month: editFormData.expected_month || null,
        expected_start_date: editFormData.expected_start_date || null,
        state_licenses: editFormData.state_licenses.length > 0 ? editFormData.state_licenses : null,
        notes: editFormData.notes.trim() || null,
      });
      setEditingProspect(null);
      await loadData();
      showToast('Prospect updated successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to update prospect', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteProspect = async () => {
    if (!deletingProspect) return;

    setProcessing(deletingProspect.id);

    try {
      const profileIdToDelete = deleteAlsoProfile && deletingProspect.loan_officer_id
        ? String(deletingProspect.loan_officer_id)
        : undefined;

      await api.deleteProspect(deletingProspect.id, profileIdToDelete);
      setDeletingProspect(null);
      setDeleteAlsoProfile(false);
      await loadData();

      const message = profileIdToDelete
        ? 'Prospect and Loan Officer profile deleted successfully!'
        : 'Prospect deleted successfully!';
      showToast(message, 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete prospect', 'error');
    } finally {
      setProcessing(null);
    }
  };

  // ==================== Sortable Header ====================

  const SortableHeader: React.FC<{
    column: SortColumn;
    label: string;
    className?: string;
  }> = ({ column, label, className = '' }) => {
    const isActive = sortColumn === column;
    return (
      <th
        className={`px-2 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive ? (
            <ChevronDownIcon
              className={`h-3.5 w-3.5 text-lumin-primary transition-transform ${
                sortDirection === 'desc' ? 'rotate-180' : ''
              }`}
            />
          ) : (
            <ChevronUpDownIcon className="h-3.5 w-3.5 text-slate-300" />
          )}
        </div>
      </th>
    );
  };

  // ==================== Render Date Cell ====================

  const DateCell: React.FC<{
    entry: ProspectWithTimeline;
    field: 'sponsor_date' | 'training_date' | 'leads_live_date' | 'unsponsor_date';
  }> = ({ entry, field }) => {
    const timeline = entry.timeline;
    if (!timeline) return <td className="px-2 py-2 text-xs text-slate-300">—</td>;

    const isEditing = editingCell?.id === timeline.id && editingCell?.field === field;
    const displayValue = timeline[field];
    const isSaving = saving === timeline.id;

    if (isEditing) {
      return (
        <td className="px-2 py-2">
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleDateKeyDown}
              autoFocus
              className="w-28 px-2 py-1 text-xs border border-lumin-primary rounded-lg focus:outline-none focus:ring-1 focus:ring-lumin-primary"
            />
            <button
              onClick={handleSaveDateEdit}
              className="p-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              title="Save"
            >
              <CheckIcon className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={handleCancelDateEdit}
              className="p-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
              title="Cancel"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      );
    }

    const hasValue = displayValue !== null;

    return (
      <td
        className={`px-2 py-2 cursor-pointer hover:bg-slate-50 transition-colors ${isSaving ? 'opacity-50' : ''}`}
        onClick={() => !isSaving && handleStartDateEdit(timeline.id, field, timeline[field])}
      >
        <span className={`text-xs ${hasValue ? 'text-slate-900 font-medium' : 'text-slate-300'}`}>
          {formatShortDate(displayValue)}
        </span>
      </td>
    );
  };

  // ==================== Render ====================

  return (
    <AdminShell
      title="Pipeline"
      description="Recruiting pipeline and onboarding readiness"
      nav={navigation}
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/recruits/new')}
            className="inline-flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-2xl text-sm font-semibold shadow hover:bg-amber-600"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Recruit
          </button>
          <button
            onClick={() => navigate('/admin/prospects/new')}
            className="inline-flex items-center gap-2 bg-white text-lumin-primary px-4 py-2 rounded-2xl text-sm font-semibold shadow"
          >
            <PlusIcon className="h-4 w-4" />
            Add Prospect
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* VA Upcoming Prospects Widget */}
        {isVA && (
          <div className="mb-2">
            <UpcomingProspects />
          </div>
        )}

        {/* Search and Hide Live Toggle */}
        <div className="rounded-3xl border border-slate-200 bg-white p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-2 flex-1">
            <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or NMLS"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 border-none focus:ring-0 text-sm"
            />
          </div>

          <button
            onClick={() => setHideLive(!hideLive)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-2xl text-sm font-medium transition-colors ${
              hideLive
                ? 'bg-lumin-primary text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <EyeSlashIcon className="h-4 w-4" />
            {hideLive ? 'Show Live' : 'Hide Live'}
            {stageCounts.live > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${hideLive ? 'bg-white/20' : 'bg-slate-200'}`}>
                {stageCounts.live}
              </span>
            )}
          </button>
        </div>

        {/* Stage Summary */}
        <div className="flex flex-wrap gap-2">
          {(Object.keys(stageConfig) as PipelineStage[])
            .sort((a, b) => stageConfig[a].order - stageConfig[b].order)
            .map(stage => {
              const count = stageCounts[stage];
              if (count === 0) return null;
              return (
                <span
                  key={stage}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${stageConfig[stage].color}`}
                >
                  {stageConfig[stage].label}
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/40">
                    {count}
                  </span>
                </span>
              );
            })}
        </div>

        {/* Table */}
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              Loading pipeline…
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No prospects found matching your filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f7f9fc] text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <SortableHeader column="month" label="Month" className="px-3 w-[85px]" />
                    <SortableHeader column="name" label="Name" className="w-[130px]" />
                    <SortableHeader column="stage" label="Stage" className="w-[100px]" />
                    <SortableHeader column="contact" label="Contact" className="w-[140px]" />
                    <SortableHeader column="nmls" label="NMLS" className="w-[70px]" />
                    <SortableHeader column="states" label="States" className="w-[50px]" />
                    <SortableHeader column="company_email" label="Company Email" className="w-[160px]" />
                    <SortableHeader column="start_date" label="Start" className="w-[75px]" />
                    <SortableHeader column="unsponsor_date" label="Unsponsor" className="w-[75px]" />
                    <SortableHeader column="sponsor_date" label="Sponsor" className="w-[75px]" />
                    <SortableHeader column="training_date" label="Training" className="w-[75px]" />
                    <SortableHeader column="live_date" label="Live" className="w-[75px]" />
                    <th className="px-2 py-3 text-center w-[45px]">Notes</th>
                    <th className="px-2 py-3 text-left w-[130px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredData.map(entry => {
                    const { prospect, stage } = entry;
                    const stageInfo = stageConfig[stage];
                    const isPreConversion = preConversionStages.includes(stage);

                    return (
                      <tr key={prospect.id} className={`hover:bg-[#f9fbff] ${getRowHighlight(entry)}`}>
                        {/* Month */}
                        <td className="px-3 py-2">
                          <span className="text-xs font-medium text-slate-600">
                            {formatMonthYear(getMonthDate(entry))}
                          </span>
                        </td>
                        {/* Name */}
                        <td className="px-2 py-2">
                          <div className="font-semibold text-slate-900 truncate">
                            {prospect.first_name} {prospect.last_name}
                          </div>
                          <div className="text-xs text-slate-400 truncate">{prospect.title}</div>
                        </td>
                        {/* Stage */}
                        <td className="px-2 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${stageInfo.color}`}>
                            {stage === 'email_created' && !prospect.company_email
                              ? 'Create Email'
                              : stageInfo.label}
                          </span>
                        </td>
                        {/* Contact */}
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-600 truncate">{prospect.email || '—'}</span>
                            {stage === 'email_created' && prospect.email && (
                              <button
                                onClick={() => handleResendWelcomeEmail(prospect.id)}
                                disabled={processing === prospect.id}
                                className="flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold text-lumin-primary px-1.5 py-0.5 rounded bg-[#e6f2f7] hover:bg-[#d0e7f0] whitespace-nowrap"
                                title="Resend welcome email to personal email"
                              >
                                <PaperAirplaneIcon className="h-2.5 w-2.5" />
                                Resend
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-slate-400">{formatPhone(prospect.phone)}</div>
                        </td>
                        {/* NMLS */}
                        <td className="px-2 py-2 text-xs text-slate-600">{prospect.nmls || '—'}</td>
                        {/* States */}
                        <td className="px-2 py-2 text-center">
                          {prospect.state_licenses && prospect.state_licenses.length > 0 ? (
                            <HoverPopover
                              title="Licensed States"
                              content={formatStateAbbreviations(prospect.state_licenses)}
                              trigger={
                                <button className="inline-flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-full bg-lumin-primary/10 text-lumin-primary text-xs font-medium hover:bg-lumin-primary/20 transition-colors">
                                  <MapPinIcon className="h-3 w-3" />
                                  <span>{prospect.state_licenses.length}</span>
                                </button>
                              }
                            />
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        {/* Company Email */}
                        <td className="px-2 py-2">
                          {(stage === 'email_created' && !prospect.company_email) || editingExistingEmails.has(prospect.id) ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="email"
                                placeholder="name@luminlending.com"
                                className="w-full px-2 py-1 rounded-lg border border-slate-200 text-xs focus:ring-1 focus:ring-lumin-primary focus:border-lumin-primary"
                                value={editingEmails.get(prospect.id) || ''}
                                onChange={e => handleEmailInputChange(prospect.id, e.target.value)}
                              />
                              <button
                                onClick={() => handleSaveEmail(prospect)}
                                disabled={processing === prospect.id}
                                className="p-1 rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex-shrink-0"
                                title="Save"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              {editingExistingEmails.has(prospect.id) && (
                                <button
                                  onClick={() => handleCancelEditEmail(prospect.id)}
                                  className="p-1 rounded-lg text-slate-500 bg-slate-100 hover:bg-slate-200 flex-shrink-0"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ) : prospect.company_email ? (
                            <div className="flex items-center gap-1 group">
                              <span className="text-xs text-slate-600 truncate block" title={prospect.company_email}>
                                {prospect.company_email}
                              </span>
                              <button
                                onClick={() => handleEditEmail(prospect)}
                                className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit email"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        {/* Start Date */}
                        <td className="px-2 py-2 text-xs text-slate-600">
                          {formatShortDate(prospect.expected_start_date)}
                        </td>
                        {/* Timeline Dates */}
                        <DateCell entry={entry} field="unsponsor_date" />
                        <DateCell entry={entry} field="sponsor_date" />
                        <DateCell entry={entry} field="training_date" />
                        <DateCell entry={entry} field="leads_live_date" />
                        {/* Notes */}
                        <td className="px-2 py-2 text-center">
                          {prospect.notes ? (
                            <HoverPopover
                              title="Notes"
                              content={prospect.notes}
                              trigger={
                                <button className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                                  <DocumentTextIcon className="h-4 w-4" />
                                </button>
                              }
                            />
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1">
                            {/* Pre-conversion actions only */}
                            {isPreConversion && (
                              <>
                                {stage === 'recruit' && (
                                  <button
                                    onClick={() => handlePromoteRecruit(prospect.id)}
                                    disabled={processing === prospect.id}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-500 px-2 py-1 rounded-lg hover:bg-amber-600 whitespace-nowrap"
                                  >
                                    <ArrowUpIcon className="h-3 w-3" />
                                    Promote
                                  </button>
                                )}
                                {stage === 'pending' && (
                                  <button
                                    onClick={() => handleStartOnboarding(prospect.id)}
                                    disabled={processing === prospect.id}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-lumin-primary px-2 py-1 rounded-lg hover:bg-lumin-primary/90 whitespace-nowrap"
                                  >
                                    <PlayIcon className="h-3 w-3" />
                                    Start
                                  </button>
                                )}
                                {stage === 'email_created' && prospect.company_email && (
                                  <button
                                    onClick={() => handleSendEmail(prospect.id)}
                                    disabled={processing === prospect.id}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-lumin-primary px-2 py-1 rounded-lg bg-[#e6f2f7] hover:bg-[#d0e7f0] whitespace-nowrap"
                                  >
                                    <PaperAirplaneIcon className="h-3 w-3" />
                                    Send
                                  </button>
                                )}
                                {['onboarding_sent', 'form_submitted'].includes(stage) && prospect.company_email && (
                                  <button
                                    onClick={() => handleSendEmail(prospect.id)}
                                    disabled={processing === prospect.id}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-lumin-primary px-2 py-1 rounded-lg bg-[#e6f2f7] hover:bg-[#d0e7f0] whitespace-nowrap"
                                  >
                                    <PaperAirplaneIcon className="h-3 w-3" />
                                    Resend
                                  </button>
                                )}
                                <button
                                  onClick={() => handleOpenEdit(prospect)}
                                  className="p-1.5 rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => setDeletingProspect(prospect)}
                                  className="p-1.5 rounded-lg text-rose-600 bg-rose-50 hover:bg-rose-100"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {/* Post-conversion: only edit button */}
                            {!isPreConversion && (
                              <button
                                onClick={() => handleOpenEdit(prospect)}
                                className="p-1.5 rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200"
                                title="Edit"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-end gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-l-2 border-l-amber-400 bg-amber-50" />
            <span>Sponsor within 7 days</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-l-2 border-l-rose-400 bg-rose-50" />
            <span>Sponsor passed, not live</span>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingProspect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingProspect(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Edit prospect</p>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {editingProspect.first_name} {editingProspect.last_name}
                </h3>
              </div>
              <button onClick={() => setEditingProspect(null)} className="text-slate-400 hover:text-slate-600">
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {['first_name', 'last_name', 'preferred_first_name', 'phone', 'email', 'nmls'].map(field => (
                <div key={field}>
                  <label className="text-sm text-slate-500 font-medium capitalize">{field.replace(/_/g, ' ')}</label>
                  <input
                    type="text"
                    name={field}
                    value={editFormData[field as keyof typeof editFormData]}
                    onChange={e =>
                      setEditFormData(prev => ({ ...prev, [field]: e.target.value }))
                    }
                    placeholder={field === 'preferred_first_name' ? 'Nickname or what they go by' : undefined}
                    className="mt-1 px-3 py-2 rounded-2xl border border-slate-200 w-full"
                  />
                </div>
              ))}
              <div>
                <label className="text-sm text-slate-500 font-medium">Title</label>
                <select
                  name="title"
                  value={editFormData.title}
                  onChange={e =>
                    setEditFormData(prev => ({ ...prev, title: e.target.value as ProspectTitle }))
                  }
                  className="mt-1 px-3 py-2 rounded-2xl border border-slate-200 w-full"
                >
                  <option value="Sr Mortgage Broker">Sr Mortgage Broker</option>
                  <option value="Executive Branch Manager">Executive Branch Manager</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium">Work Location</label>
                <div className="mt-2 flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="place_of_work"
                      value="In Office"
                      checked={editFormData.place_of_work === 'In Office'}
                      onChange={e => setEditFormData(prev => ({ ...prev, place_of_work: e.target.value as PlaceOfWork }))}
                      className="h-4 w-4 text-lumin-primary border-slate-300 focus:ring-lumin-primary"
                    />
                    <span className="text-sm text-slate-700">In Office</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="place_of_work"
                      value="Remote"
                      checked={editFormData.place_of_work === 'Remote'}
                      onChange={e => setEditFormData(prev => ({ ...prev, place_of_work: e.target.value as PlaceOfWork }))}
                      className="h-4 w-4 text-lumin-primary border-slate-300 focus:ring-lumin-primary"
                    />
                    <span className="text-sm text-slate-700">Remote</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium">Expected Sponsor Date</label>
                <input
                  type="date"
                  name="expected_month"
                  value={editFormData.expected_month}
                  onChange={e =>
                    setEditFormData(prev => ({ ...prev, expected_month: e.target.value }))
                  }
                  className="mt-1 px-3 py-2 rounded-2xl border border-slate-200 w-full"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium">Expected Start Date</label>
                <input
                  type="date"
                  name="expected_start_date"
                  value={editFormData.expected_start_date}
                  onChange={e =>
                    setEditFormData(prev => ({ ...prev, expected_start_date: e.target.value }))
                  }
                  className="mt-1 px-3 py-2 rounded-2xl border border-slate-200 w-full"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm text-slate-500 font-medium">State Licenses</label>
              <div className="relative mt-1" ref={editLicenseDropdownRef}>
                <div
                  onClick={() => setEditLicenseDropdownOpen(!editLicenseDropdownOpen)}
                  className="min-h-[42px] w-full rounded-2xl border border-slate-200 px-3 py-2 cursor-pointer flex items-center justify-between gap-2 bg-white"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {editFormData.state_licenses.length === 0 ? (
                      <span className="text-slate-400">Select states...</span>
                    ) : (
                      editFormData.state_licenses.map(state => (
                        <span
                          key={state}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lumin-primary/10 text-lumin-primary text-xs font-medium"
                        >
                          {state}
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleEditRemoveLicense(state);
                            }}
                            className="hover:bg-lumin-primary/20 rounded-full p-0.5"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${editLicenseDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
                {editLicenseDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-2xl shadow-lg max-h-48 overflow-y-auto">
                    {COMPANY_LICENSES.map(state => (
                      <div
                        key={state}
                        onClick={() => handleEditLicenseToggle(state)}
                        className={`px-3 py-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                          editFormData.state_licenses.includes(state) ? 'bg-lumin-primary/5' : ''
                        }`}
                      >
                        <span className="text-sm text-slate-700">{state}</span>
                        {editFormData.state_licenses.includes(state) && (
                          <span className="text-lumin-primary text-sm">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {editFormData.state_licenses.length} state{editFormData.state_licenses.length !== 1 ? 's' : ''} selected
              </p>
            </div>

            <div className="mt-4">
              <label className="text-sm text-slate-500 font-medium">Notes (internal)</label>
              <textarea
                name="notes"
                value={editFormData.notes}
                onChange={e => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes for VAs and admins..."
                rows={3}
                className="mt-1 px-3 py-2 rounded-2xl border border-slate-200 w-full resize-none"
              />
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setEditingProspect(null)}
                className="px-4 py-2 rounded-2xl border border-slate-200 text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={processing === editingProspect.id}
                className="px-4 py-2 rounded-2xl bg-lumin-primary text-white"
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deletingProspect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingProspect(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Confirm deletion</p>
                <h3 className="text-2xl font-semibold text-slate-900">Delete Prospect</h3>
              </div>
              <button onClick={() => { setDeletingProspect(null); setDeleteAlsoProfile(false); }} className="text-slate-400 hover:text-slate-600 text-2xl">
                &times;
              </button>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 mb-6">
              <p className="text-rose-800">
                Are you sure you want to delete <strong>{deletingProspect.first_name} {deletingProspect.last_name}</strong>?
              </p>
              <p className="text-rose-600 text-sm mt-2">
                This action cannot be undone. All associated onboarding data will also be deleted.
              </p>
            </div>

            {deletingProspect.status === 'converted' && deletingProspect.loan_officer_id && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteAlsoProfile}
                    onChange={e => setDeleteAlsoProfile(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <p className="text-amber-800 font-medium">Also delete their Loan Officer profile</p>
                    <p className="text-amber-600 text-sm mt-1">
                      This prospect was converted to a Loan Officer. Check this box to also remove their LO account, onboarding progress, and all associated data.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setDeletingProspect(null); setDeleteAlsoProfile(false); }}
                className="px-4 py-2 rounded-2xl border border-slate-200 text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProspect}
                disabled={processing === deletingProspect.id}
                className="px-4 py-2 rounded-2xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {processing === deletingProspect.id ? 'Deleting...' : (deleteAlsoProfile ? 'Delete Both' : 'Delete Prospect')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AdminShell>
  );
};

export default Prospects;
